/**
 * BillingFinancial/index.tsx
 * Internal financial dashboard — 5 tabs: Deferred Revenue, MRR/ARR,
 * Collections, QuickBooks, StaxBill Import.
 * Calls velaro-messaging superadmin/billing/* endpoints via api.messaging.
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

// ── Types ──────────────────────────────────────────────────────────────────────

type DashTab = "upcoming" | "collections" | "deferred" | "mrr" | "quickbooks" | "staxbill";

interface FinancialSummary {
  deferred: { balanceCents: number; recognizedThisMonthCents: number };
  mrr: { mrrCents: number; arrCents: number };
  collections: { openCents: number; overdueCount: number; paidThisMonthCents: number };
  quickBooks: { pendingSyncCount: number; failedSyncCount: number };
}

interface DeferredScheduleRow {
  year: number;
  month: number;
  scheduledCents: number;
  count: number;
}

interface AgingBucket {
  bucket: string;
  label: string;
  count: number;
  amountCents: number;
}

interface DunningPipelineRow {
  state: string;
  count: number;
}

interface TierRow {
  packageId: number;
  count: number;
  mrrCents: number;
}

interface UpcomingRenewal {
  id: number;
  siteId: number;
  company: string | null;
  email: string | null;
  billingInterval: string;
  billedAmount: number;
  paymentMethod: string;
  nextRenewalDate: string;
  daysUntilRenewal: number;
  parallelBillingMode: boolean;
  externalBillingSystem: string | null;
}

interface OpenInvoice {
  id: number;
  siteId: number;
  company: string;
  email: string | null;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  amountDue: number;
  totalAmount: number;
  status: string;
  daysOverdue: number;
  dunningState: string;
  agingBucket: string;
}

interface TierRow {
  packageId: number;
  count: number;
  mrrCents: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function monthName(m: number): string {
  return new Date(2000, m - 1, 1).toLocaleString("en-US", { month: "short" });
}

const MONTH_OPTS = Array.from({ length: 12 }, (_, i) => i + 1);
const YEAR_OPTS = [2024, 2025, 2026, 2027];

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  color = "slate",
}: {
  label: string;
  value: string;
  sub?: string;
  color?: "slate" | "emerald" | "orange" | "blue";
}) {
  const textColors = {
    slate: "text-slate-800",
    emerald: "text-emerald-600",
    orange: "text-orange-600",
    blue: "text-blue-600",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${textColors[color]}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Deferred Revenue Tab ───────────────────────────────────────────────────────

function DeferredTab({
  summary,
  api,
  toastAlert,
}: {
  summary: FinancialSummary | null;
  api: ReturnType<typeof useApi>;
  toastAlert: ReturnType<typeof useToastAlert>;
}) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [schedule, setSchedule] = useState<DeferredScheduleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [recognizing, setRecognizing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get(
        `superadmin/billing/deferred-revenue?month=${month}&year=${year}`
      );
      if (resp.ok) {
        const data = await resp.json();
        setSchedule(Array.isArray(data.schedule) ? data.schedule : []);
      }
    } catch {
      setSchedule([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging, month, year]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRecognize() {
    if (
      !confirm(
        `Mark all unrecognized deferred revenue entries for ${monthName(month)} ${year} as recognized? This cannot be undone.`
      )
    )
      return;
    setRecognizing(true);
    try {
      const resp = await api.messaging.post("superadmin/billing/recognize-revenue", {
        month,
        year,
      });
      if (resp.ok) {
        const data = await resp.json();
        toastAlert.displayToast(
          Severity.Success,
          `Recognized ${data.entriesRecognized} entries (${fmt(data.totalCents ?? 0)})`
        );
        load();
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to run revenue recognition.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setRecognizing(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          label="Deferred Revenue Balance"
          value={fmt(summary?.deferred.balanceCents ?? 0)}
          sub="Total unrecognized"
          color="blue"
        />
        <StatCard
          label="Recognized This Month"
          value={fmt(summary?.deferred.recognizedThisMonthCents ?? 0)}
          sub="Posted to income"
          color="emerald"
        />
      </div>

      {/* Schedule */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <p className="text-sm font-medium text-slate-700">Upcoming Recognition Schedule</p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="fa-solid fa-spinner fa-spin text-slate-400" />
          </div>
        ) : schedule.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">No scheduled entries.</p>
        ) : (
          <>
            <div className="grid grid-cols-[80px_80px_1fr_80px] gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
              <span>Year</span>
              <span>Month</span>
              <span className="text-right">Scheduled</span>
              <span className="text-right">Entries</span>
            </div>
            <div className="divide-y divide-slate-100">
              {schedule.map((row) => (
                <div
                  key={`${row.year}-${row.month}`}
                  className="grid grid-cols-[80px_80px_1fr_80px] gap-3 px-4 py-2 items-center"
                >
                  <span className="text-sm text-slate-700">{row.year}</span>
                  <span className="text-sm text-slate-700">{monthName(row.month)}</span>
                  <span className="text-sm font-medium text-slate-800 text-right tabular-nums">
                    {fmt(row.scheduledCents)}
                  </span>
                  <span className="text-sm text-slate-500 text-right">{row.count}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Month-end recognition */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-sm font-medium text-slate-700 mb-3">Run Month-End Recognition</p>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTH_OPTS.map((m) => (
              <option key={m} value={m}>
                {monthName(m)}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {YEAR_OPTS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <button
            onClick={handleRecognize}
            disabled={recognizing}
            className="px-4 py-2 rounded-md bg-cornflower-blue-600 text-white text-sm font-medium hover:bg-cornflower-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {recognizing && <i className="fa-solid fa-spinner fa-spin" />}
            Post Recognition
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Marks all unrecognized entries for the chosen month/year as recognized. Idempotent.
        </p>
      </div>
    </div>
  );
}

// ── MRR/ARR Tab ───────────────────────────────────────────────────────────────

function MrrTab({ summary }: { summary: FinancialSummary | null }) {
  const [tiers, setTiers] = useState<TierRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [collected, setCollected] = useState({ last: 0, this: 0 });
  const api = useApi();

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.messaging.get("superadmin/billing/mrr-report");
        if (resp.ok) {
          const data = await resp.json();
          setTiers(Array.isArray(data.byTier) ? data.byTier : []);
          setCollected({
            last: data.lastMonthCollectedCents ?? 0,
            this: data.thisMonthCollectedCents ?? 0,
          });
        }
      } catch {
        setTiers([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [api.messaging]);

  const packageNames: Record<number, string> = { 1: "Starter", 2: "Professional", 3: "Enterprise" };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          label="MRR"
          value={fmt(summary?.mrr.mrrCents ?? 0)}
          sub="Monthly recurring revenue"
          color="blue"
        />
        <StatCard
          label="ARR"
          value={fmt(summary?.mrr.arrCents ?? 0)}
          sub="Annualized"
          color="blue"
        />
        <StatCard
          label="Last Month Collected"
          value={fmt(collected.last)}
          sub="Cash collected"
          color="emerald"
        />
        <StatCard
          label="This Month Collected"
          value={fmt(collected.this)}
          sub="Cash collected"
          color="emerald"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <p className="text-sm font-medium text-slate-700">MRR by Plan Tier</p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="fa-solid fa-spinner fa-spin text-slate-400" />
          </div>
        ) : tiers.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">No active subscriptions.</p>
        ) : (
          <>
            <div className="grid grid-cols-[1fr_100px_130px] gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
              <span>Plan</span>
              <span className="text-right">Accounts</span>
              <span className="text-right">MRR</span>
            </div>
            <div className="divide-y divide-slate-100">
              {tiers.map((t) => (
                <div
                  key={t.packageId}
                  className="grid grid-cols-[1fr_100px_130px] gap-3 px-4 py-3 items-center"
                >
                  <span className="text-sm font-medium text-slate-700">
                    {packageNames[t.packageId] ?? `Package ${t.packageId}`}
                  </span>
                  <span className="text-sm text-slate-600 text-right">{t.count}</span>
                  <span className="text-sm font-bold text-slate-800 text-right tabular-nums">
                    {fmt(t.mrrCents)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Upcoming Renewals Tab ─────────────────────────────────────────────────────

function UpcomingTab() {
  const [renewals, setRenewals] = useState<UpcomingRenewal[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const api = useApi();

  const load = async (d: number) => {
    setLoading(true);
    try {
      const resp = await api.messaging.get(`superadmin/billing/upcoming?days=${d}`);
      if (resp.ok) {
        const data = await resp.json();
        setRenewals(Array.isArray(data.renewals) ? data.renewals : []);
      }
    } catch {
      setRenewals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days, api.messaging]);

  const totalMrr = renewals.reduce((s, r) => s + (r.billedAmount ?? 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Show next</span>
          {[7, 14, 30, 60, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                days === d
                  ? "bg-cornflower-blue-600 text-white border-cornflower-blue-600"
                  : "border-slate-300 text-slate-600 hover:border-cornflower-blue-400"
              }`}
            >
              {d} days
            </button>
          ))}
        </div>
        <div className="text-sm text-slate-500">
          <span className="font-semibold text-slate-800">{renewals.length}</span> upcoming ·{" "}
          <span className="font-semibold text-emerald-600">${(totalMrr).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span> total
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_100px_120px_100px_80px] gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
          <span>Customer</span>
          <span>Interval</span>
          <span className="text-right">Amount</span>
          <span>Renewal Date</span>
          <span>Payment</span>
          <span className="text-center">Days</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="fa-solid fa-spinner fa-spin text-slate-400" />
          </div>
        ) : renewals.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">No upcoming renewals in this window.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {renewals.map((r) => (
              <div key={r.id} className="grid grid-cols-[2fr_1fr_100px_120px_100px_80px] gap-3 px-4 py-3 items-center">
                <div>
                  <p className="text-sm font-medium text-slate-800 truncate">{r.company ?? `Site ${r.siteId}`}</p>
                  <p className="text-xs text-slate-400 truncate">{r.email}</p>
                  {r.parallelBillingMode && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">parallel mode</span>
                  )}
                </div>
                <span className="text-xs text-slate-600 capitalize">{r.billingInterval}</span>
                <span className="text-sm font-semibold text-slate-800 text-right tabular-nums">
                  ${(r.billedAmount ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-sm text-slate-600">
                  {r.nextRenewalDate ? new Date(r.nextRenewalDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                </span>
                <span className="text-xs capitalize text-slate-600">{r.paymentMethod}</span>
                <span className={`text-sm font-bold text-center ${r.daysUntilRenewal <= 3 ? "text-orange-600" : "text-slate-700"}`}>
                  {r.daysUntilRenewal}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {renewals.some(r => r.parallelBillingMode) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          Accounts in <strong>parallel mode</strong> will generate invoices internally but emails are suppressed.
          Use the StaxBill Import tab to cut them over when ready.
        </div>
      )}
    </div>
  );
}

// ── Collections Tab ───────────────────────────────────────────────────────────

function CollectionsTab({ summary }: { summary: FinancialSummary | null }) {
  const [invoices, setInvoices] = useState<OpenInvoice[]>([]);
  const [dunning, setDunning] = useState<DunningPipelineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [bucketFilter, setBucketFilter] = useState<string>("all");
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const api = useApi();
  const toastAlert = useToastAlert();

  const load = async () => {
    setLoading(true);
    try {
      const [invResp, colResp] = await Promise.all([
        api.messaging.get("superadmin/billing/invoices-open"),
        api.messaging.get("superadmin/billing/collections-report"),
      ]);
      if (invResp.ok) {
        const data = await invResp.json();
        setInvoices(Array.isArray(data) ? data : []);
      }
      if (colResp.ok) {
        const data = await colResp.json();
        setDunning(Array.isArray(data.dunningPipeline) ? data.dunningPipeline : []);
      }
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [api.messaging]);

  const markPaid = async (inv: OpenInvoice) => {
    setActionLoading(inv.id);
    try {
      const resp = await api.messaging.post(`superadmin/billing/invoice/${inv.id}/pay`, {
        amountCents: inv.amountDue,
        notes: "Manual payment recorded via Financial Dashboard"
      });
      const data = await resp.json();
      if (data.success) {
        toastAlert.displayToast(Severity.Success, `Invoice ${inv.invoiceNumber} marked paid — dunning stopped.`);
        await load();
      } else {
        toastAlert.displayToast(Severity.Error, data.error ?? "Failed to mark paid");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error");
    } finally {
      setActionLoading(null);
    }
  };

  const resend = async (inv: OpenInvoice) => {
    setActionLoading(inv.id);
    try {
      const resp = await api.messaging.post(`superadmin/billing/invoice/${inv.id}/resend`, {});
      const data = await resp.json();
      if (data.success) {
        toastAlert.displayToast(Severity.Success, `Invoice resent to ${data.sentTo}`);
      } else {
        toastAlert.displayToast(Severity.Error, data.error ?? "Failed to resend");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error");
    } finally {
      setActionLoading(null);
    }
  };

  const BUCKETS = [
    { id: "all", label: "All" },
    { id: "current", label: "Current" },
    { id: "1-30", label: "1–30 days" },
    { id: "31-60", label: "31–60 days" },
    { id: "61-90", label: "61–90 days" },
    { id: "90+", label: "90+ days" },
  ];

  const filtered = bucketFilter === "all" ? invoices : invoices.filter(i => i.agingBucket === bucketFilter);

  const bucketColor = (bucket: string) => {
    if (bucket === "current") return "text-slate-700 bg-slate-100";
    if (bucket === "1-30") return "text-yellow-700 bg-yellow-100";
    if (bucket === "31-60") return "text-orange-700 bg-orange-100";
    return "text-red-700 bg-red-100";
  };

  const dunningBadge = (state: string) => {
    if (!state || state === "none") return null;
    const colors: Record<string, string> = {
      warn1: "bg-yellow-100 text-yellow-700",
      warn2: "bg-orange-100 text-orange-700",
      suspended: "bg-red-100 text-red-700",
    };
    return (
      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${colors[state] ?? "bg-slate-100 text-slate-600"}`}>
        {state}
      </span>
    );
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <StatCard
          label="Total Open"
          value={fmt(summary?.collections.openCents ?? 0)}
          sub="Outstanding invoices"
          color="blue"
        />
        <StatCard
          label="Overdue"
          value={String(summary?.collections.overdueCount ?? 0)}
          sub="Past due date"
          color={summary?.collections.overdueCount ? "orange" : "slate"}
        />
        <StatCard
          label="Paid This Month"
          value={fmt(summary?.collections.paidThisMonthCents ?? 0)}
          sub="Cash collected"
          color="emerald"
        />
      </div>

      {/* Dunning pipeline summary */}
      {dunning.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          {dunning.map((d) => (
            <div key={d.state} className="bg-white border border-slate-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500 capitalize">{d.state?.replace(/_/g, " ")}</span>
              <span className="text-sm font-bold text-slate-800">{d.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Bucket filter */}
      <div className="flex items-center gap-2 flex-wrap">
        {BUCKETS.map((b) => {
          const cnt = b.id === "all" ? invoices.length : invoices.filter(i => i.agingBucket === b.id).length;
          return (
            <button
              key={b.id}
              onClick={() => setBucketFilter(b.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                bucketFilter === b.id
                  ? "bg-cornflower-blue-600 text-white border-cornflower-blue-600"
                  : "border-slate-300 text-slate-600 hover:border-cornflower-blue-400"
              }`}
            >
              {b.label} ({cnt})
            </button>
          );
        })}
        <button
          onClick={load}
          className="ml-auto px-3 py-1 rounded-full text-xs border border-slate-300 text-slate-600 hover:bg-slate-50"
        >
          <i className="fa-solid fa-arrows-rotate mr-1" /> Refresh
        </button>
      </div>

      {/* Invoice drilldown table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_90px_90px_100px_1fr_150px] gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
          <span>Customer</span>
          <span>Invoice #</span>
          <span className="text-right">Amount</span>
          <span className="text-center">Days Over</span>
          <span>Dunning</span>
          <span>Due Date</span>
          <span className="text-right">Actions</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="fa-solid fa-spinner fa-spin text-slate-400" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">No open invoices.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((inv) => (
              <div key={inv.id} className="grid grid-cols-[2fr_1fr_90px_90px_100px_1fr_150px] gap-2 px-4 py-3 items-center">
                <div>
                  <p className="text-sm font-medium text-slate-800 truncate">{inv.company}</p>
                  <p className="text-xs text-slate-400 truncate">{inv.email}</p>
                </div>
                <span className="text-xs font-mono text-slate-600">{inv.invoiceNumber}</span>
                <span className="text-sm font-semibold text-slate-800 text-right tabular-nums">
                  {fmt(inv.amountDue)}
                </span>
                <span className={`text-sm font-bold text-center ${inv.daysOverdue > 60 ? "text-red-600" : inv.daysOverdue > 30 ? "text-orange-600" : inv.daysOverdue > 0 ? "text-yellow-600" : "text-slate-500"}`}>
                  {inv.daysOverdue > 0 ? inv.daysOverdue : "—"}
                </span>
                <span>{dunningBadge(inv.dunningState)}</span>
                <span className="text-xs text-slate-600">
                  {new Date(inv.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </span>
                <div className="flex items-center gap-1 justify-end">
                  <button
                    onClick={() => markPaid(inv)}
                    disabled={actionLoading === inv.id}
                    title="Mark Paid — stops dunning immediately"
                    className="px-2 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {actionLoading === inv.id ? <i className="fa-solid fa-spinner fa-spin" /> : "Mark Paid"}
                  </button>
                  <button
                    onClick={() => resend(inv)}
                    disabled={actionLoading === inv.id}
                    title="Resend invoice email"
                    className="px-2 py-1 text-xs rounded border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Resend
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400">
        Mark Paid records the payment and immediately stops dunning for that account.
        Use this when a check, ACH, or wire is received outside of Stripe.
      </p>
    </div>
  );
}

// ── QuickBooks Tab ─────────────────────────────────────────────────────────────

function QuickBooksTab({ summary, onRefresh }: { summary: FinancialSummary | null; onRefresh: () => void }) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [syncing, setSyncing] = useState(false);

  async function handleSync() {
    setSyncing(true);
    try {
      const resp = await api.messaging.post("superadmin/billing/sync-quickbooks", {});
      if (resp.ok) {
        const data = await resp.json();
        toastAlert.displayToast(
          Severity.Success,
          `Synced ${data.synced} invoice(s)${data.failed > 0 ? ` · ${data.failed} failed` : ""}`
        );
        onRefresh();
      } else {
        toastAlert.displayToast(Severity.Error, "QuickBooks sync failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setSyncing(false);
    }
  }

  const pendingCount = summary?.quickBooks.pendingSyncCount ?? 0;
  const failedCount = summary?.quickBooks.failedSyncCount ?? 0;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          label="Pending QB Sync"
          value={String(pendingCount)}
          sub="Paid invoices not yet pushed"
          color={pendingCount > 0 ? "orange" : "slate"}
        />
        <StatCard
          label="Failed Sync"
          value={String(failedCount)}
          sub="Sync errors to review"
          color={failedCount > 0 ? "orange" : "slate"}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-sm font-medium text-slate-700 mb-1">Sync Invoices to QuickBooks</p>
        <p className="text-xs text-slate-400 mb-4">
          Pushes up to 100 paid invoices that haven't been synced yet. Skips invoices with a
          previous sync failure — review those manually.
        </p>
        <button
          onClick={handleSync}
          disabled={syncing || pendingCount === 0}
          className="px-4 py-2 rounded-md bg-cornflower-blue-600 text-white text-sm font-medium hover:bg-cornflower-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {syncing ? (
            <i className="fa-solid fa-spinner fa-spin" />
          ) : (
            <i className="fa-brands fa-connectdevelop" />
          )}
          Sync Now {pendingCount > 0 && `(${pendingCount} pending)`}
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        <p className="font-medium mb-1">QuickBooks connection</p>
        <p>
          To connect or reconnect QuickBooks, go to <strong>SuperAdmin → Billing → Site Detail</strong>{" "}
          and use the Connect QuickBooks button for the Velaro internal site. The connection is
          per-site and uses OAuth 2.0.
        </p>
      </div>
    </div>
  );
}

// ── StaxBill Import Tab ───────────────────────────────────────────────────────

const BILLING_INTERVALS = ["monthly", "quarterly", "annual", "biannual", "triennial"];
const PAYMENT_METHODS = ["invoice", "stripe", "ach", "check"];

function StaxBillTab() {
  const api = useApi();
  const toastAlert = useToastAlert();

  const [email, setEmail] = useState("");
  const [staxId, setStaxId] = useState("");
  const [contractStart, setContractStart] = useState("");
  const [contractEnd, setContractEnd] = useState("");
  const [interval, setInterval] = useState("annual");
  const [monthlyPrice, setMonthlyPrice] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("invoice");
  const [notes, setNotes] = useState("");
  const [packageId, setPackageId] = useState("2");
  const [siteId, setSiteId] = useState<number | null>(null);
  const [findingsite, setFindingSite] = useState(false);
  const [importing, setImporting] = useState(false);

  async function handleFindSite() {
    if (!email.trim()) return;
    setFindingSite(true);
    setSiteId(null);
    try {
      const resp = await api.admin.get(
        `api/Site/find-by-email?email=${encodeURIComponent(email.trim())}`
      );
      if (resp.ok) {
        const data = await resp.json();
        setSiteId(data.siteId ?? null);
        if (!data.siteId) toastAlert.displayToast(Severity.Warning, "No site found for that email.");
      } else {
        toastAlert.displayToast(Severity.Error, "Lookup failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setFindingSite(false);
    }
  }

  async function handleImport() {
    if (!siteId) {
      toastAlert.displayToast(Severity.Error, "Find a site first.");
      return;
    }
    if (!contractStart || !monthlyPrice) {
      toastAlert.displayToast(Severity.Error, "Contract Start and Monthly Price are required.");
      return;
    }
    setImporting(true);
    try {
      const resp = await api.messaging.post(`superadmin/billing/site/${siteId}/migrate`, {
        ExternalId: staxId || `staxbill-import-${siteId}`,
        ExternalSystem: "staxbill",
        ContractStartDate: contractStart,
        ContractEndDate: contractEnd || null,
        BillingInterval: interval,
        BaseMonthlyPrice: parseFloat(monthlyPrice),
        PaymentMethod: paymentMethod,
        PackageId: parseInt(packageId, 10),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          toastAlert.displayToast(
            Severity.Success,
            `Site ${siteId} migrated from Staxbill (subscription ${data.subscriptionId})`
          );
          // Reset form
          setEmail("");
          setStaxId("");
          setContractStart("");
          setContractEnd("");
          setMonthlyPrice("");
          setNotes("");
          setSiteId(null);
        } else {
          toastAlert.displayToast(Severity.Error, data.error ?? "Import failed.");
        }
      } else {
        toastAlert.displayToast(Severity.Error, "Import failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <p className="text-sm font-medium text-slate-700">Migrate a Staxbill Account to Velaro Billing</p>

        {/* Find site */}
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-500 mb-1">Customer Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@example.com"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500"
            />
          </div>
          <button
            onClick={handleFindSite}
            disabled={findingsite || !email.trim()}
            className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
          >
            {findingsite && <i className="fa-solid fa-spinner fa-spin" />}
            Find Site
          </button>
        </div>

        {siteId && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-sm text-emerald-700">
            Found: <strong>Site {siteId}</strong>
          </div>
        )}

        {/* Staxbill ID */}
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Staxbill Account ID</label>
          <input
            type="text"
            value={staxId}
            onChange={(e) => setStaxId(e.target.value)}
            placeholder="Optional — used as ExternalId reference"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500"
          />
        </div>

        {/* Contract dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Contract Start *</label>
            <input
              type="date"
              value={contractStart}
              onChange={(e) => setContractStart(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Contract End</label>
            <input
              type="date"
              value={contractEnd}
              onChange={(e) => setContractEnd(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500"
            />
          </div>
        </div>

        {/* Billing */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Interval</label>
            <select
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500"
            >
              {BILLING_INTERVALS.map((i) => (
                <option key={i} value={i}>
                  {i.charAt(0).toUpperCase() + i.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Monthly Price *</label>
            <input
              type="number"
              value={monthlyPrice}
              onChange={(e) => setMonthlyPrice(e.target.value)}
              placeholder="249.00"
              step="0.01"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Payment Method</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500"
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Package</label>
          <select
            value={packageId}
            onChange={(e) => setPackageId(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500"
          >
            <option value="1">Starter</option>
            <option value="2">Professional</option>
            <option value="3">Enterprise</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Migration notes…"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500"
          />
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleImport}
            disabled={importing || !siteId}
            className="px-5 py-2 rounded-md bg-cornflower-blue-600 text-white text-sm font-medium hover:bg-cornflower-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {importing && <i className="fa-solid fa-spinner fa-spin" />}
            Import to Velaro Billing
          </button>
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-500 space-y-1">
        <p>This creates a <code>BillingSubscription</code> with <code>ExternalBillingSystem = "staxbill"</code> and status <code>"active"</code>.</p>
        <p>After import, enable <strong>Parallel Billing Mode</strong> (below) to suppress email/QB during verification. Once confirmed, click <strong>Cut Over</strong> to go live.</p>
      </div>

      {/* Cutover section */}
      {siteId && (
        <CutoverSection siteId={siteId} />
      )}
    </div>
  );
}

function CutoverSection({ siteId }: { siteId: number }) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [parallelMode, setParallelMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cutting, setCutting] = useState(false);

  async function toggleParallelMode(val: boolean) {
    setSaving(true);
    try {
      const resp = await api.messaging.put(`superadmin/billing/site/${siteId}/subscription`, {
        ParallelBillingMode: val,
      });
      if (resp.ok) {
        setParallelMode(val);
        toastAlert.displayToast(
          Severity.Success,
          val ? "Parallel mode enabled — invoices generated but suppressed." : "Parallel mode disabled."
        );
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to update parallel mode.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setSaving(false);
    }
  }

  async function handleCutover() {
    if (!confirm(`Cut over site ${siteId} to full Velaro billing? This disables parallel mode and begins live invoice delivery.`)) return;
    setCutting(true);
    try {
      const resp = await api.messaging.post(`superadmin/billing/site/${siteId}/cutover`, {});
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, `Site ${siteId} cut over to full Velaro billing.`);
        setParallelMode(false);
      } else {
        toastAlert.displayToast(Severity.Error, "Cutover failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setCutting(false);
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
      <p className="text-sm font-medium text-slate-700">Parallel Mode &amp; Cutover — Site {siteId}</p>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-700">Parallel Billing Mode</p>
          <p className="text-xs text-slate-400">
            Generate invoices locally but suppress customer email and QuickBooks sync.
          </p>
        </div>
        <button
          onClick={() => toggleParallelMode(!parallelMode)}
          disabled={saving}
          className={`px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 transition-colors ${
            parallelMode
              ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          {saving ? <i className="fa-solid fa-spinner fa-spin" /> : parallelMode ? "Enabled" : "Disabled"}
        </button>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <button
          onClick={handleCutover}
          disabled={cutting}
          className="px-5 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
        >
          {cutting && <i className="fa-solid fa-spinner fa-spin" />}
          <i className="fa-solid fa-check-double" />
          Cut Over to Velaro Billing
        </button>
        <p className="text-xs text-slate-400 mt-1.5">
          Disables parallel mode, sets BillingCutoverDate to today, and begins full invoice/email/QB processing.
        </p>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function BillingFinancial() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [activeTab, setActiveTab] = useState<DashTab>("upcoming");
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const resp = await api.messaging.get("superadmin/billing/financial-summary");
      if (resp.ok) setSummary(await resp.json());
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const TABS: { id: DashTab; label: string; icon: string }[] = [
    { id: "upcoming", label: "Upcoming", icon: "fa-calendar-days" },
    { id: "collections", label: "Collections", icon: "fa-file-invoice-dollar" },
    { id: "deferred", label: "Deferred Revenue", icon: "fa-calendar-check" },
    { id: "mrr", label: "MRR / ARR", icon: "fa-chart-line" },
    { id: "quickbooks", label: "QuickBooks", icon: "fa-book" },
    { id: "staxbill", label: "StaxBill Import", icon: "fa-arrow-right-arrow-left" },
  ];

  return (
    <div className="h-full overflow-y-auto">
      <div className="border-b border-slate-200 px-6 pt-5 pb-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Financial Dashboard</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Deferred revenue, MRR/ARR, collections, QuickBooks, and billing imports.
            </p>
          </div>
          <button
            onClick={loadSummary}
            disabled={summaryLoading}
            className="px-3 py-2 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1"
            title="Refresh summary"
          >
            <i className={`fa-solid fa-arrows-rotate ${summaryLoading ? "fa-spin" : ""}`} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                activeTab === tab.id
                  ? "border-cornflower-blue-500 text-cornflower-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <i className={`fa-solid ${tab.icon} text-xs`} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 py-5">
        {activeTab === "upcoming" && <UpcomingTab />}
        {activeTab === "collections" && <CollectionsTab summary={summary} />}
        {activeTab === "deferred" && (
          <DeferredTab summary={summary} api={api} toastAlert={toastAlert} />
        )}
        {activeTab === "mrr" && <MrrTab summary={summary} />}
        {activeTab === "quickbooks" && (
          <QuickBooksTab summary={summary} onRefresh={loadSummary} />
        )}
        {activeTab === "staxbill" && <StaxBillTab />}
      </div>
    </div>
  );
}
